import {
  LocationStatus,
  OperationType,
} from '@bufteam/cfacorp_d2c.bufbuild_es/cfa/d2c/frontend/restaurant/v1/restaurant_pb';
import type { HoursOfOperation as ProtobufHoursOfOperation } from '@bufteam/cfacorp_d2c.bufbuild_es/cfa/d2c/frontend/restaurant/v1/restaurant_pb';
import type {
  LocationSummaryResponse,
  RestaurantSearchResponse,
} from '@bufteam/cfacorp_d2c.bufbuild_es/cfa/d2c/frontend/restaurant/v1/restaurant_service_pb';
import * as z from 'zod';

import type { HoursOfOperation, LocationSummary, StandardHours } from '../types/location';
import { LocationResult } from './schemas';

const locationStatusToString: Record<LocationStatus, string> = {
  [LocationStatus.UNSPECIFIED]: 'unspecified',
  [LocationStatus.OPEN]: 'open',
  [LocationStatus.CLOSED]: 'closed',
  [LocationStatus.REMODEL]: 'remodel',
  [LocationStatus.FUTURE]: 'future',
  [LocationStatus.NEVEROPENED]: 'neveropened',
};

export const locationSummaryResponseToLocationSummary = z.transform<
  LocationSummaryResponse['locationSummaries'][number],
  LocationSummary
>((i) => ({
  locationNumber: i.locationNumber,
  name: i.name,
  status: locationStatusToString[i.status] ?? 'unspecified',
}));

const operationTypeToString: Record<OperationType, string> = {
  [OperationType.UNSPECIFIED]: 'closed',
  [OperationType.STANDARDHOURS]: 'standardHours',
  [OperationType.OPEN24HOURS]: 'open24Hours',
  [OperationType.CLOSED]: 'closed',
};

const mapDayOfOperation = (day: {
  operationType: OperationType;
  operatingInterval?: unknown;
  blackoutHours?: unknown;
}): StandardHours => ({
  operationType: operationTypeToString[day.operationType] ?? 'closed',
  operatingInterval: day.operatingInterval as StandardHours['operatingInterval'],
  blackoutHours: day.blackoutHours as StandardHours['blackoutHours'],
});

const mapHoursOfOperation = (hours?: ProtobufHoursOfOperation): HoursOfOperation => ({
  monday: hours?.monday ? mapDayOfOperation(hours.monday) : undefined!,
  tuesday: hours?.tuesday ? mapDayOfOperation(hours.tuesday) : undefined!,
  wednesday: hours?.wednesday ? mapDayOfOperation(hours.wednesday) : undefined!,
  thursday: hours?.thursday ? mapDayOfOperation(hours.thursday) : undefined!,
  friday: hours?.friday ? mapDayOfOperation(hours.friday) : undefined!,
  saturday: hours?.saturday ? mapDayOfOperation(hours.saturday) : undefined!,
  holidays: hours?.holidays?.map((h) => ({
    ...h,
    holidayHours: h.holidayHours ? mapDayOfOperation(h.holidayHours) : undefined!,
  })),
});

export const restaurantSearchResponseToLocationResult = z.transform<
  RestaurantSearchResponse['restaurants'][number],
  LocationResult
>((i) => ({
  locationNumber: i.locationNumber,
  name: i.name,
  marketableName: i.name,
  operator: {
    name: i.operator?.name ?? '',
    personId: i.operator?.personId ?? '',
  },
  locationContact: {
    streetAddress: i.locationContact?.streetAddress,
    daytimePhone: i.locationContact?.daytimePhone && {
      areaCode: i.locationContact.daytimePhone.areaCode,
      phoneNumber: i.locationContact.daytimePhone.phoneNumber,
      base: i.locationContact.daytimePhone.base,
      extensionNumber: i.locationContact.daytimePhone.extensionNumber,
    },
    emailAddress: i.locationContact?.emailAddress ?? '',
  },
  status: String(i.status),
  openDate: '',
  projectedOpenDate: i.projectedOpenDate,
  marketableURL: i.marketableUrl,
  gps: {
    latitude: i.gps?.latitude ?? 0,
    longitude: i.gps?.longitude ?? 0,
  },
  conceptCode: String(i.conceptCode),
  locationOperatingAs: String(i.locationOperatingAs),
  dflsUsed: i.dflsUsed,
  marketName: '',
  marketLevelMarketCode: '',
  operatorTeamName: '',
  operatorTeamCode: '',
  serviceTeamName: '',
  serviceTeamCode: '',
  mobileOrderingSettings: {
    status: String(i.mobileOrderSettings?.status ?? ''),
    maxTotalAmount: i.mobileOrderSettings?.maxTotalAmount ?? 0,
    menuWarnings: i.mobileOrderSettings?.menuWarnings?.map(String) ?? [],
    fulfillmentMethods: {
      cateringPickup: i.mobileOrderSettings?.fulfillmentMethods?.cateringPickup
        ? {
            ...i.mobileOrderSettings.fulfillmentMethods.cateringPickup,
            hoursOfOperation: mapHoursOfOperation(
              i.mobileOrderSettings.fulfillmentMethods.cateringPickup.hoursOfOperation,
            ),
            specialEvents: i.mobileOrderSettings.fulfillmentMethods.cateringPickup.specialEvents?.map(
              (event) => ({
                ...event,
                specialEventHours: event.specialEventHours
                  ? mapDayOfOperation(event.specialEventHours)
                  : undefined!,
              }),
            ),
          }
        : undefined,
      cateringDelivery: i.mobileOrderSettings?.fulfillmentMethods?.cateringDelivery
        ? {
            ...i.mobileOrderSettings.fulfillmentMethods.cateringDelivery,
            hoursOfOperation: mapHoursOfOperation(
              i.mobileOrderSettings.fulfillmentMethods.cateringDelivery.hoursOfOperation,
            ),
            specialEvents: i.mobileOrderSettings.fulfillmentMethods.cateringDelivery.specialEvents?.map(
              (event) => ({
                ...event,
                specialEventHours: event.specialEventHours
                  ? mapDayOfOperation(event.specialEventHours)
                  : undefined!,
              }),
            ),
          }
        : undefined,
    },
    menuCustomization: i.mobileOrderSettings?.menuCustomization ?? false,
    isNotificationDelayed: i.mobileOrderSettings?.isNotificationDelayed ?? false,
  },
  restaurantHours: {
    hoursOfOperation: i.restaurantHours?.hoursOfOperation
      ? mapHoursOfOperation(i.restaurantHours.hoursOfOperation)
      : ({} as HoursOfOperation),
  },
  timeZone: i.timeZone,
  digitalFeatures: {
    servesBreakfast: i.digitalFeatures?.servesBreakfast ?? false,
    servesLimitedMenu: false,
    offersMobileOrdering: i.digitalFeatures?.offersMobileOrdering ?? false,
    offersMobilePayment: i.digitalFeatures?.offersMobilePayment ?? false,
    offersCustomerWifi: i.digitalFeatures?.offersCustomerWifi ?? false,
  },
  physicalFeatures: {
    playground: String(i.physicalFeatures?.playground ?? ''),
    hasDriveThru: i.physicalFeatures?.hasDriveThru ?? false,
    hasDiningRoom: i.physicalFeatures?.hasDiningRoom ?? false,
  },
  taxType: String(i.taxType),
  supportedLanguages: [],
  restaurantImageURL: i.restaurantImageUrl,
  activeFlags: [...i.activeFlags],
  nearbyLocations: [...i.nearbyLocations],
  locationCode: String(i.locationCode),
  locationFormat: String(i.locationFormat),
  locationSubtypeCode: String(i.locationSubtypeCode),
}));
